/**
 * Board settings sheet — owner-only.
 *
 * Owns everything that's about CUSTOMISING a board (rather than
 * sharing or doing the work):
 *   - Visual theme presets (palette tints)
 *   - Accent hue override (single colour, modulates the chosen preset)
 *   - Danger zone: archive / restore
 *
 * Lives behind a gear icon in the BoardHeader. Used to live in the
 * ShareSheet which made no sense — sharing a board and re-painting it
 * are different jobs.
 */

import { useState } from 'react';
import { Modal } from './Modal';
import { api } from '../api';
import { THEMES, applyTheme } from '../lib/themes';
import type { BoardDetail } from '../../shared/types';

export function BoardSettingsSheet({
  open,
  onClose,
  board,
  onBoardChange,
}: {
  open: boolean;
  onClose: () => void;
  board: BoardDetail;
  onBoardChange: (next: BoardDetail) => void;
}) {
  const isOwner = board.role === 'owner';
  if (!isOwner) {
    // Render an empty modal stating the gate, rather than nothing —
    // a non-owner who hits the gear icon should know why.
    return (
      <Modal open={open} onClose={onClose} title="Board settings" maxWidth={420}>
        <p className="text-sm text-cream-3">
          Only the board owner can change visual settings or archive the board.
        </p>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Board settings" maxWidth={520}>
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Visual theme</h3>
          <p className="text-xs text-cream-3">
            Re-tones this board only; other boards aren't affected.
          </p>
          <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-5">
            {THEMES.map((t) => {
              const on = (board.visualTheme ?? 'ember') === t.id;
              return (
                <button
                  type="button"
                  key={t.id}
                  className="flex flex-col items-stretch overflow-hidden rounded-lg border text-left"
                  style={{
                    borderColor: on
                      ? `${t.vars['--color-clove']}cc`
                      : 'var(--color-ink-rim)',
                    background: t.vars['--color-ink-2'],
                    color: t.vars['--color-cream'],
                  }}
                  onClick={async () => {
                    onBoardChange({ ...board, visualTheme: t.id });
                    applyTheme({
                      boardTheme: t.id,
                      boardAccentHue: board.accentHue,
                    });
                    try {
                      await api.patchBoardCosmetic(board.id, { visualTheme: t.id });
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                  title={t.description}
                >
                  <div
                    className="h-10 w-full"
                    style={{
                      background: `linear-gradient(135deg, ${t.vars['--color-ink-2']} 30%, ${t.vars['--color-clove']} 130%)`,
                    }}
                  />
                  <div className="px-2 py-1.5 text-xs">{t.label}</div>
                </button>
              );
            })}
          </div>
          <AccentRow board={board} onBoardChange={onBoardChange} />
        </section>

        <PrioritySection board={board} onBoardChange={onBoardChange} />

        {board.phase !== 'archived' ? (
          <section className="flex flex-col gap-2 border-t border-ink-rim/60 pt-4">
            <h3 className="text-sm font-medium text-rust">Danger zone</h3>
            <p className="text-xs text-cream-3">
              Archive hides this board from members' home pages but keeps everything
              accessible via direct link.
            </p>
            <button
              type="button"
              className="btn btn-ghost mt-1 self-start text-xs text-rust"
              onClick={async () => {
                if (
                  !confirm(
                    'Archive this board? It will be hidden from your home by default. Members can still find it via direct link.',
                  )
                )
                  return;
                try {
                  await api.setPhase(board.id, 'archived');
                  onBoardChange({ ...board, phase: 'archived' });
                  onClose();
                } catch (e) {
                  alert(e instanceof Error ? e.message : 'Failed');
                }
              }}
            >
              Archive board
            </button>
          </section>
        ) : (
          <section className="flex flex-col gap-2 border-t border-ink-rim/60 pt-4">
            <h3 className="text-sm font-medium">Archived</h3>
            <p className="text-xs text-cream-3">
              This board is hidden from members' home pages.
            </p>
            <button
              type="button"
              className="btn btn-ghost mt-1 self-start text-xs"
              onClick={async () => {
                try {
                  await api.setPhase(board.id, 'shipped');
                  onBoardChange({ ...board, phase: 'shipped' });
                } catch (e) {
                  alert(e instanceof Error ? e.message : 'Failed');
                }
              }}
            >
              Restore (move back to Shipped)
            </button>
          </section>
        )}
      </div>
    </Modal>
  );
}

const PRIORITY_TIERS: Array<{
  value: import('../../shared/types').BoardPriority;
  label: string;
  hint: string;
}> = [
  {
    value: 'background',
    label: 'Background',
    hint: 'Fit it in. SynFolio happily plans other boards over the top.',
  },
  {
    value: 'normal',
    label: 'Normal',
    hint: 'Standard board. Hard-blocks other normal boards and the calendar.',
  },
  {
    value: 'focus',
    label: 'Focus right now',
    hint: 'Books over background boards. Asks before clashing with other normal boards.',
  },
];

function PrioritySection({
  board,
  onBoardChange,
}: {
  board: BoardDetail;
  onBoardChange: (next: BoardDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function set(value: import('../../shared/types').BoardPriority) {
    if (busy || board.priority === value) return;
    setBusy(true);
    setError(null);
    const prev = board.priority;
    // Optimistic update — the priority change is cheap and we want
    // the picker to feel instant; rollback if the API rejects.
    onBoardChange({ ...board, priority: value });
    try {
      await api.updateBoard(board.id, { priority: value });
    } catch (e) {
      onBoardChange({ ...board, priority: prev });
      setError(e instanceof Error ? e.message : 'Failed to update priority');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-2 border-t border-ink-rim/60 pt-4">
      <h3 className="text-sm font-medium">Priority</h3>
      <p className="text-xs text-cream-3">
        Tells SynFolio how aggressively this board's planner can book over your
        other commitments. Changes the cross-board scheduling behaviour everywhere.
      </p>
      <div className="mt-1 grid gap-1.5 sm:grid-cols-3">
        {PRIORITY_TIERS.map((t) => {
          const on = (board.priority ?? 'normal') === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => void set(t.value)}
              disabled={busy}
              className={`flex flex-col items-start gap-1 rounded-lg border p-2.5 text-left transition ${
                on
                  ? 'border-clove ring-1 ring-clove'
                  : 'border-ink-rim hover:border-cream-4'
              } ${busy ? 'opacity-70' : ''}`}
            >
              <span className="text-xs font-medium">{t.label}</span>
              <span className="text-[10px] text-cream-3">{t.hint}</span>
            </button>
          );
        })}
      </div>
      {error && <p className="text-xs text-rust">{error}</p>}
    </section>
  );
}

function AccentRow({
  board,
  onBoardChange,
}: {
  board: BoardDetail;
  onBoardChange: (next: BoardDetail) => void;
}) {
  const cur = board.accentHue;
  return (
    <div className="mt-3 flex flex-col gap-1.5 border-t border-ink-rim/60 pt-3">
      <label className="text-xs text-cream-3">
        Accent override {cur != null ? `· ${cur}°` : '· none (use theme default)'}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={0}
          max={359}
          value={cur ?? 0}
          onChange={(e) => {
            const v = Number(e.target.value);
            onBoardChange({ ...board, accentHue: v });
            applyTheme({
              boardTheme: board.visualTheme,
              boardAccentHue: v,
            });
          }}
          onMouseUp={async () => {
            try {
              await api.patchBoardCosmetic(board.id, { accentHue: board.accentHue });
            } catch (e) {
              console.error(e);
            }
          }}
          onTouchEnd={async () => {
            try {
              await api.patchBoardCosmetic(board.id, { accentHue: board.accentHue });
            } catch (e) {
              console.error(e);
            }
          }}
          className="flex-1"
        />
        {cur != null && (
          <button
            type="button"
            className="btn btn-ghost text-xs"
            onClick={async () => {
              onBoardChange({ ...board, accentHue: null });
              applyTheme({ boardTheme: board.visualTheme });
              await api.patchBoardCosmetic(board.id, { accentHue: null }).catch(console.error);
            }}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
